/**
 * WorkflowList - left-panel list of every workflow.
 */

import { useMemo } from 'react'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import type { WorkflowDefinitionAdmin } from '@/admin/hooks/use-workflow-definitions'

import { formatResourceType } from './format-resource-type'

const STANDALONE_RESOURCE_TYPE = '__standalone__'

interface WorkflowListProps {
  workflows: WorkflowDefinitionAdmin[]
  selectedUuid: string | null
  onSelect: (uuid: string) => void
}

export function WorkflowList({
  workflows,
  selectedUuid,
  onSelect,
}: WorkflowListProps) {
  const groups = useMemo(() => groupByResourceType(workflows), [workflows])

  if (workflows.length === 0) {
    return (
      <p className="text-[12px] text-[var(--ink-3)]">
        No workflows yet. Create one to get started.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <div key={group.resourceType} className="flex flex-col gap-1">
          <p className="clara-section-label px-1 text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {group.resourceType === STANDALONE_RESOURCE_TYPE
              ? 'Workflows'
              : formatResourceType(group.resourceType)}
          </p>
          <div className="flex flex-col gap-0.5">
            {group.workflows.map((wf) => {
              const isActive = wf.uuid === selectedUuid
              return (
                <Button
                  key={wf.uuid}
                  variant="ghost"
                  onClick={() => onSelect(wf.uuid)}
                  data-testid={`workflows-item-${wf.uuid}`}
                  className={cn(
                    'h-auto w-full justify-start rounded-[4px] px-3 py-2 text-left text-[12px] transition-all duration-150',
                    isActive
                      ? 'bg-[var(--green-bg-subtle)] text-[var(--green)]'
                      : 'bg-transparent text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]',
                  )}
                >
                  <div className="flex w-full flex-col">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-[400]">{wf.label}</span>
                      {!wf.is_active && (
                        <span className="rounded-[3px] bg-[var(--bg-subtle)] px-1.5 py-[1px] text-[9px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                          inactive
                        </span>
                      )}
                    </div>
                    <span className="mt-0.5 truncate font-mono text-[10px] text-[var(--ink-3)]">
                      {wf.key}
                    </span>
                  </div>
                </Button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

interface Group {
  resourceType: string
  workflows: WorkflowDefinitionAdmin[]
}

function groupByResourceType(workflows: WorkflowDefinitionAdmin[]): Group[] {
  const map = new Map<string, WorkflowDefinitionAdmin[]>()
  for (const wf of workflows) {
    const resourceType = wf.resource_type || STANDALONE_RESOURCE_TYPE
    const list = map.get(resourceType) ?? []
    list.push(wf)
    map.set(resourceType, list)
  }
  const groups: Group[] = []
  for (const [resourceType, list] of map.entries()) {
    groups.push({
      resourceType,
      workflows: [...list].sort((a, b) => a.label.localeCompare(b.label)),
    })
  }
  return groups.sort((a, b) => a.resourceType.localeCompare(b.resourceType))
}
